/**
 * Timetastic
 * Nav component
 */
import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react"
import { Link } from "gatsby"
import { doSignupClick } from "../../helpers/analytics"

// Helpers
import { ClickOutside, trackCta } from "src/helpers"

// Styles
import "./nav.scss"

// Links
const links = [
  {
    label: "Why Timetastic?",
    links: [
      {
        description: "A brief overview of Timetastic's features",
        href: "/how-it-works/",
        label: "How it Works",
      },
      {
        description: "Keeping track of time off work, simplified",
        href: "/absence-management-software/",
        label: "Absence management software",
      },
      {
        description: "Manage annual leave on desktop or mobile",
        href: "/staff-leave-planner/",
        label: "Staff leave planner",
      },
    ],
  },
  {
    href: "/pricing/",
    label: "Pricing",
  },
  {
    href: "/about/",
    label: "About",
  },
  {
    label: "Learn",
    links: [
      {
        description: "The user manual, how to use Timetastic",
        href: "https://help.timetastic.co.uk",
        label: "Help Centre",
      },
      {
        description: "Our customers tell us what makes them tick",
        href: "https://timetastic.co.uk/blog/tag/customer-stories",
        label: "Success Stories",
      },
      {
        description: "Exploring workplace culture and time off",
        href: "https://timetastic.co.uk/blog/",
        label: "Blog",
      },
      {
        description: "See the latest features we’ve released",
        href: "https://changelog.timetastic.co.uk",
        label: "Product Updates",
      },
      {
        description: "Get support from our friendly team",
        href: "/support/",
        label: "Contact Us",
      },
    ],
  },
  {
    hideOnDesktop: true,
    href: "https://app.timetastic.co.uk/login/",
    label: "Log In",
    trackCta: "LoginTopNav",
  },
]

// JSX
const Nav = () => {
  // Refs
  const refNavContainer = useRef(null)
  const refButton = useRef(null)
  const refHamburgerContent = useRef(null)
  const refFirstMenuItem = useRef(null)

  // State for our nav
  const [navOpen, setNavOpen] = useState(false)
  const [subnavOpen, setSubNavOpen] = useState(false)
  const [scrollPositionWhenSubNavOpened, setScrollPositionWhenSubNavOpened] =
    useState(undefined)

  const classes = {
    navOpen: "nav-open",
  }

  const getSubNavButtons = () => {
    if (!refHamburgerContent || !refHamburgerContent.current) {
      return undefined
    }

    return refHamburgerContent.current.querySelectorAll("[aria-haspopup]")
  }

  // Set a class on `html` element so we can globally style the page when
  // the nav is open/closed
  if (typeof document !== "undefined") {
    if (navOpen === true) {
      document.documentElement.classList.add(classes.navOpen)
    } else {
      document.documentElement.classList.remove(classes.navOpen)
    }
  }

  // Check for Esc key press and close the nav
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.keyCode === 27 || event.keyCode === 32) {
        // Close subnav
        if (subnavOpen === true) {
          setSubNavOpen(false)
          closeAllSubNavButtons()
        } else {
          // Close hamburger nav
          if (navOpen === true) {
            setNavOpen(false)
            refButton.current.focus()
          }
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  })

  const closeAllSubNavButtons = () => {
    const buttons = getSubNavButtons()
    if (buttons) {
      for (let i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute("aria-expanded", false)
      }
    }
  }

  // Close the subnav when user clicks outside of it
  function handleClickOutside() {
    if (subnavOpen === true) {
      setSubNavOpen(false)
      closeAllSubNavButtons()
    } else {
      return
    }
  }

  // Check for tab key press and if the nav isn't open then skip
  // to the next viable element (or previous link if shift is held down)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.keyCode === 9) {
        if (navOpen === false && subnavOpen === false) {
          if (!event.shiftKey) {
            refButton.current.focus()
          }
        }
      }
    }
    const x = refHamburgerContent.current
    x.addEventListener("keyup", handleKeyDown)

    return () => {
      x.removeEventListener("keyup", handleKeyDown)
    }
  }, [navOpen, subnavOpen])

  // Check if the nav should be hidden from screenreaders
  // Check for value set by CSS to know if we are in
  // desktop-nav or mobile-nav mode
  // KNOWN BUG - see README file
  const isAriaHidden = useMemo(() => {
    if (typeof window !== "undefined") {
      const viewport = window
        .getComputedStyle(document.querySelector("body"), ":after")
        .getPropertyValue("content")
        .replace(/"/g, "")

      if (viewport === "desktop-nav") {
        return false
      } else {
        return !navOpen
      }
    } else {
      return false
    }
  }, [navOpen])

  // Show/Hide the child menu and let assistive tech know it has been toggled
  const toggleChildLinks = (event) => {
    const buttons = getSubNavButtons()
    const button = event.currentTarget
    const isActive = button.getAttribute("aria-expanded")

    if (!buttons) {
      return
    }

    if (isActive === true || isActive === "true") {
      button.setAttribute("aria-expanded", false)
      button.focus()
      setSubNavOpen(false)
      setScrollPositionWhenSubNavOpened(undefined)
    } else {
      for (let i = 0; i < buttons.length; i++) {
        if (buttons[i] === button) {
          button.setAttribute("aria-expanded", true)
          button.focus()
          setSubNavOpen(true)
          setScrollPositionWhenSubNavOpened(
            document.body.getBoundingClientRect().top
          )
        } else {
          buttons[i].setAttribute("aria-expanded", false)
        }
      }
    }
  }

  // Turn off subnav once the user starts to scroll
  // TODO - don't run if the mobile nav button is visible
  const listener = (event) => {
    const scrollPoint = document.body.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const cutOff = parseInt(0 - windowHeight / 3, 10)

    const scrollPosition = scrollPoint.top
    const scrolledTooFarDown =
      cutOff + scrollPositionWhenSubNavOpened > scrollPosition
    const scrolledTooFarUp =
      scrollPosition > scrollPositionWhenSubNavOpened - cutOff

    if (scrolledTooFarDown || scrolledTooFarUp) {
      setSubNavOpen(false)
      closeAllSubNavButtons()
    }
  }

  // Hide the dropdown menu if the user scrolls past it
  useLayoutEffect(() => {
    window.addEventListener("scroll", listener)
    return () => {
      window.removeEventListener("scroll", listener)
    }
  })

  return (
    <ClickOutside callback={handleClickOutside} className="c-nav-container">
      <div className="c-nav-container__flex" ref={refNavContainer}>
        <nav className="c-nav">
          <div
            aria-hidden={isAriaHidden}
            className="c-nav__hamburger-content"
            data-open={navOpen}
            ref={refHamburgerContent}
          >
            <ul
              aria-labelledby="nav-button"
              className="c-nav__ul"
              id="nav"
              role="menu"
            >
              {links.map((link, index) => (
                <Fragment key={`link-${index}`}>
                  {link.links && (
                    <li
                      className="c-nav__li c-nav__li--children"
                      role="menuitem"
                    >
                      <button
                        aria-controls={`child-nav-${index}`}
                        aria-haspopup={true}
                        aria-expanded={false}
                        className="c-nav__link"
                        id={`nav-item-${index}`}
                        ref={index === 0 ? refFirstMenuItem : null}
                        type="button"
                        onClick={toggleChildLinks}
                      >
                        {link.label}{" "}
                        <svg
                          fill="none"
                          height="8"
                          viewBox="0 0 14 8"
                          width="14"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            clipRule="evenodd"
                            d="m.292893.292893c.390524-.3905241 1.023687-.3905241 1.414217 0l5.29289 5.292897 5.2929-5.292897c.3905-.3905241 1.0237-.3905241 1.4142 0 .3905.390524.3905 1.023687 0 1.414217l-5.99999 6c-.39053.39052-1.02369.39052-1.41422 0l-5.999997-6c-.3905241-.39053-.3905241-1.023693 0-1.414217z"
                            fill="currentColor"
                            fillRule="evenodd"
                          />
                        </svg>
                      </button>
                      <ul
                        aria-labelledby={`nav-item-${index}`}
                        role="menu"
                        className={`c-nav__child-ul c-nav__child-ul--${link.label
                          .replace(/ /g, "")
                          .toLowerCase()}`}
                        id={`child-nav-${index}`}
                      >
                        {link.links.map((childLink, childIndex) => (
                          <li
                            className="c-nav__child-li"
                            ref={index === 0 ? refFirstMenuItem : null}
                            key={`link-${index}-child-${childIndex}`}
                            role="menuitem"
                          >
                            {childLink.href.startsWith("http") && (
                              <a
                                className="c-nav__child-link"
                                href={childLink.href}
                                role="menuitem"
                                onClick={
                                  childLink.trackCta
                                    ? () => {
                                        trackCta(childLink.trackCta)
                                      }
                                    : undefined
                                }
                              >
                                <b className="c-nav__child-title">
                                  {childLink.label}
                                </b>
                                <div className="c-nav__child-description">
                                  {childLink.description}
                                </div>
                                <svg
                                  aria-hidden="true"
                                  className="c-nav__child-icon"
                                  fill="none"
                                  height="14"
                                  viewBox="0 0 8 14"
                                  width="8"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <use xlinkHref="#svg-arrow-simple" />
                                </svg>
                              </a>
                            )}
                            {!childLink.href.startsWith("http") && (
                              <Link
                                activeClassName="is-active"
                                className="c-nav__child-link"
                                role="menuitem"
                                to={childLink.href}
                                onClick={
                                  childLink.trackCta
                                    ? () => {
                                        trackCta(childLink.trackCta)
                                      }
                                    : undefined
                                }
                              >
                                <b className="c-nav__child-title">
                                  {childLink.label}
                                </b>
                                <div className="c-nav__child-description">
                                  {childLink.description}
                                </div>
                                <svg
                                  aria-hidden="true"
                                  className="c-nav__child-icon"
                                  fill="none"
                                  height="14"
                                  viewBox="0 0 8 14"
                                  width="8"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <use xlinkHref="#svg-arrow-simple" />
                                </svg>
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    </li>
                  )}
                  {!link.links && (
                    <li
                      className={`c-nav__li${
                        link.hideOnDesktop ? " c-nav__li--mobile-only" : ""
                      }`}
                      role="none"
                    >
                      {link.href.startsWith("http") && (
                        <a
                          className="c-nav__link"
                          href={link.href}
                          role="menuitem"
                          onClick={
                            link.trackCta
                              ? () => {
                                  trackCta(link.trackCta)
                                }
                              : undefined
                          }
                        >
                          {link.label}
                        </a>
                      )}
                      {!link.href.startsWith("http") && (
                        <Link
                          activeClassName="is-active"
                          className="c-nav__link"
                          role="menuitem"
                          to={link.href}
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  )}
                </Fragment>
              ))}
            </ul>
            <div className="c-nav__buttons-in">
              <a
                className="c-button c-button--ghost-primary"
                href="/pricing/"
                onClick={() => {
                  trackCta("SignupTopNav")
                  doSignupClick()
                }}
              >
                Pricing & free trial
              </a>
            </div>
          </div>
        </nav>
        <div className="c-nav__buttons-out">
          <a
            className="c-nav-button"
            href="https://app.timetastic.co.uk/login/"
            onClick={() => {
              trackCta("HeaderLogin")
            }}
          >
            Log in
          </a>
          <a
            className="c-button c-button--ghost-primary"
            href="/pricing/"
            onClick={(e) => {
              trackCta("HeaderSignup")
              doSignupClick()
            }}
          >
            Pricing & free trial
          </a>
        </div>
        <button
          aria-controls="nav"
          aria-expanded={navOpen}
          aria-haspopup="true"
          className="c-button c-button--ghost c-nav__hamburger-button"
          id="nav-button"
          ref={refButton}
          type="button"
          onClick={() => {
            setNavOpen(!navOpen)
            if (navOpen !== true) {
              refHamburgerContent.current.setAttribute("tabIndex", "-1")
              refHamburgerContent.current.focus()
            }
          }}
        >
          {navOpen ? "Close" : "Menu"}
        </button>
      </div>
    </ClickOutside>
  )
}

export default Nav
